import styled from 'styled-components';

import { ActivityCenterWrapper } from '@xing-com/crate-activity-center-wrapper';
import { Header } from '@xing-com/crate-layout-header';
import { useDeviceInfo } from '@xing-com/crate-xinglet';
import { Section, Container } from '@xing-com/layout-primitives';
import { BottomBar } from '@xing-com/platform-layout-bottom-bar';
import { Footer } from '@xing-com/platform-layout-footer';
import { xdlColorBackground } from '@xing-com/tokens';
import { useMediaListener, BREAKPOINTS } from '@xing-com/util';

import { HeaderLoggedOutWithLoginLink } from './header-logged-out-with-login-link';

const StyledLayoutWrapper = styled.div`
  background-color: ${xdlColorBackground};
`;

type UnifiedLayoutProps = {
  layout: 'xing-unified' | 'xing' | 'xing-gridless';
  isLoggedInLayout: boolean;
  loginDestination?: string;
  loginTracking?: string;
};

export const UnifiedLayout: React.FC<
  React.PropsWithChildren<UnifiedLayoutProps>
> = ({
  children,
  layout,
  isLoggedInLayout,
  loginDestination,
  loginTracking,
}) => {
  const isGridless = layout === 'xing-gridless';
  const isXing = layout === 'xing';

  const breakpoint = useMediaListener();
  const isNotHandheld = breakpoint !== BREAKPOINTS.handheld;

  const { isWebview } = useDeviceInfo();

  // names get just a bit more semantic here ...
  const hasActivityCenterWrapper = !isLoggedInLayout;
  const hasLoggedInHeader = isLoggedInLayout;
  const hasLoggedOutHeader = !isLoggedInLayout;
  const hasFooter = !isLoggedInLayout || isNotHandheld;
  const hasBottomBar = !isWebview;

  // This will go away over time. Setup modern will remain
  const setups = {
    modern: children,
    modernWithSection: <Section noBlockPadding>{children}</Section>,
    legacyWithContainer: (
      <Section noBlockPadding>
        <Container>{children}</Container>
      </Section>
    ),
  };

  // LI uni, LI xing, LO uni
  let setupType: keyof typeof setups = 'modern';

  if (isGridless) {
    setupType = 'modernWithSection';
  }
  // LO xing
  if (!isLoggedInLayout && isXing) {
    setupType = 'legacyWithContainer';
  }

  return (
    <>
      {hasActivityCenterWrapper ? <ActivityCenterWrapper /> : null}
      <StyledLayoutWrapper data-qa="core-crate-layout-main">
        {hasLoggedInHeader ? <Header /> : null}
        {hasLoggedOutHeader ? (
          <HeaderLoggedOutWithLoginLink
            loginDestination={loginDestination}
            loginTracking={loginTracking}
          />
        ) : null}
        <main id="content">{setups[setupType]}</main>
        {hasFooter ? <Footer homeLink="/" /> : null}
        {hasBottomBar ? <BottomBar /> : null}
      </StyledLayoutWrapper>
    </>
  );
};
